import { Link } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Product, getSmallestSizePrice, formatPrice, localizeProduct, resolveAssetUrl } from "../lib/api";
import { useCartStore } from "../store/cart";
import { useGuestCartStore } from "../store/guestCart";
import { useCurrencyStore } from "../store/currency";
import { useAuthStore } from "../store/auth";
import { getTenantConfig } from "../lib/tenant";
import toast from "react-hot-toast";

interface Props {
  product: Product;
}

export default function ProductCard({ product }: Props) {
  const { add, toggleOpen } = useCartStore();
  const guestCart = useGuestCartStore();
  const { user } = useAuthStore();
  const { currency, siteMode } = useCurrencyStore();
  const tenant = getTenantConfig();
  const { i18n } = useTranslation();
  const { name } = localizeProduct(product, i18n.language);

  async function handleAddToCart(e: React.MouseEvent) {
    e.preventDefault();
    if (product.soldOut) return;
    if (!user) {
      guestCart.add(product, 1);
      toast.success("Added to cart!");
      guestCart.toggleOpen();
      return;
    }
    try {
      await add(product.id);
      toast.success("Added to cart!");
      toggleOpen();
    } catch {
      toast.error("Failed to add to cart");
    }
  }

  const isChlorine = tenant.id === "chlorine_fine_art";

  return (
    <Link
      to={`/shop/${product.slug}`}
      className={`group relative flex flex-col overflow-hidden transition-all duration-300 ${isChlorine ? "bg-transparent hover:translate-y-[-4px]" : "rounded-2xl bg-white border border-gray-100 shadow-sm card-hover"}`}
    >
      <div className={`relative aspect-[4/5] overflow-hidden bg-gray-50 ${!isChlorine ? "rounded-t-2xl" : ""}`}>
        <img
          src={resolveAssetUrl(product.imageUrl)}
          alt={name}
          className={`h-full w-full object-cover transition-transform duration-500 ${product.soldOut ? "opacity-50" : "group-hover:scale-110"}`}
          loading="lazy"
        />
        {product.soldOut && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="bg-gray-900/80 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
              Sold Out
            </span>
          </div>
        )}
      </div>

      <div className={`flex flex-col gap-1.5 ${isChlorine ? "pt-6 pb-2" : "p-4"}`}>
        {product.categoryName && (
          <span className="text-[10px] font-bold uppercase tracking-widest opacity-50" style={{ color: 'rgb(var(--brand-color))' }}>
            {product.categoryName}
          </span>
        )}
        <h3 className={`text-base font-bold text-gray-900 line-clamp-2 leading-tight ${isChlorine ? "uppercase tracking-tight" : ""}`}>
          {name}
        </h3>
        <div className="flex items-center justify-between mt-3">
          <span className="text-sm font-bold text-gray-900">
            {(() => {
              const { price, size } = getSmallestSizePrice(product, currency);
              return (
                <>
                  {size && <span className="text-[10px] font-normal text-gray-400 mr-1 lowercase tracking-wide italic">from</span>}
                  {formatPrice(price, currency)}
                </>
              );
            })()}
          </span>
          <button
            onClick={handleAddToCart}
            disabled={product.soldOut || siteMode === "read_only"}
            className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors ${
              product.soldOut || siteMode === "read_only"
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "text-white"
            }`}
            style={!product.soldOut && siteMode !== "read_only" ? { backgroundColor: 'rgb(var(--brand-color))' } : {}}
          >
            <ShoppingCart size={11} />
            {product.soldOut ? "Sold Out" : siteMode === "read_only" ? "Off" : "Add"}
          </button>
        </div>
      </div>
    </Link>
  );
}
